import { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'

// Actions
import { actions as staticPagesActions } from 'modules/staticpages/actions'

// Hooks
import { useRequest } from 'modules/requests/hooks'

// Components
import { Tabs2 } from 'kit'
import { Error, PageHeading, Entity, Shelf } from '@tracklib/kit'
import { StreamField } from 'components/staticpages/streamfield'
import SEO from 'components/base/seo'

// Types
import { Dispatch, GetState, State } from 'types'

const DiscoverLoading = () => (
  <div>
    <Shelf>
      <Shelf.Item>
        <Entity loading size={1} />
      </Shelf.Item>
    </Shelf>
    <Shelf>
      {[0, 1, 2, 3].map(x => (
        <Shelf.Item key={x}>
          <Entity loading size={4} />
        </Shelf.Item>
      ))}
    </Shelf>
  </div>
)

export const loadInitialData =
  ({
    location: {
      params: { tab },
    },
  }: {
    location: { params: { tab: string } }
  }) =>
  async (dispatch: Dispatch, getState: GetState) => {
    await dispatch(staticPagesActions.loadDiscoverSubPages())
    const { pageId } = getState().staticPages.discoverTabs[tab || 'all']
    return dispatch(staticPagesActions.loadPage(pageId))
  }

type DiscoverProps = {
  match: {
    params: {
      tab: string
    }
  }
}

const Discover = ({
  match: {
    params: { tab },
  },
}: DiscoverProps): JSX.Element => {
  const state = useSelector<State, State>(s => s)
  const dispatch = useDispatch()
  const { discoverTabs } = state.staticPages
  const currentTab = discoverTabs[tab || 'all']
  const pageId = currentTab?.pageId
  const pageSummary = currentTab?.summary
  const tabs = Object.keys(discoverTabs).map(id => discoverTabs[id])
  const page = state.entities.pages[String(pageId)]
  const fetchPageRequest = useRequest('FETCH_STATIC_PAGE', pageId)
  const fetchTabsRequest = useRequest('FETCH_DISCOVER_SUBPAGES')

  const { pathname } = useLocation()
  useEffect(() => {
    if (!tabs.length && !fetchTabsRequest.isLoading && !fetchTabsRequest.hasError) {
      dispatch(staticPagesActions.loadDiscoverSubPages())
    }
    if (!page && !fetchPageRequest.isLoading && !fetchPageRequest.hasError && pageId && !fetchPageRequest.isDone) {
      dispatch(staticPagesActions.loadPage(pageId))
    }
  }, [dispatch, fetchPageRequest, fetchTabsRequest, tabs, page, pageId])

  const tabItems = tabs?.map(x => ({
    id: x.pageId,
    label: x.title,
    to: x.slug === 'all' ? '/inspiration/' : `/inspiration/${x.slug}/`,
  }))
  const description = pageSummary || 'See below for recommended tracks for your next level production'
  return (
    <>
      <SEO title="Inspiration | Tracklib" description={description} />
      <PageHeading
        heading="Inspiration"
        subheading={tabs?.find(x => `/inspiration/${x.slug}/` === pathname)?.title}
        description={description}
      />
      {!fetchTabsRequest.hasError && page && !page?.hide_nav && <Tabs2 items={tabItems} sticky />}
      {!page && !fetchPageRequest.isDone && <DiscoverLoading />}
      {((!page && fetchPageRequest.isDone) || fetchTabsRequest.hasError) && <Error />}
      {page && <StreamField blocks={page.content} />}
    </>
  )
}

export default Discover
